import React, { useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Notification.css';

// Store a reference to the toast function
const toastRef = {
  current: {
    success: (message, options) => toast.success(message, {
      ...options,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: 'notification-toast',
      containerId: 'notification-container',
    }),
    error: (message, options) => toast.error(message, {
      ...options,
      position: 'top-right',
      autoClose: 7000, // Longer display for errors
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: 'notification-toast error-toast',
      containerId: 'notification-container',
    }),
    info: (message, options) => toast.info(message, {
      ...options,
      position: 'top-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: 'notification-toast',
      containerId: 'notification-container',
    }),
    warning: (message, options) => toast.warning(message, {
      ...options,
      position: 'top-right',
      autoClose: 6000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: 'notification-toast warning-toast',
      containerId: 'notification-container',
    }),
  }
};

// Export the toast reference so it can be used directly
export { toastRef };

/**
 * Notification Component
 * 
 * This component provides a container for toast notifications.
 * It should be placed once in your app (typically in App.js).
 * 
 * Usage:
 * 1. Import and use the useNotification hook in any component:
 *    const { showSuccess, showError, showWarning, showInfo } = useNotification();
 * 
 * 2. Show notifications:
 *    - showSuccess('Operation completed successfully!');
 *    - showError('An error occurred');
 *    - showWarning('Please check your input');
 *    - showInfo('New message received');
 * 
 * 3. Options can be passed as a second parameter to customize the notification:
 *    showSuccess('Profile updated!', { autoClose: 3000 });
 */

const Notification = () => {
  // Initialize the notification system
  useEffect(() => {
    // Set up global error handler
    const handleGlobalError = (event) => {
      const error = event.error || event.reason || 'An unexpected error occurred';
      const errorMessage = error.message || String(error);
      
      // Don't show notification for aborted requests
      if (error.name === 'AbortError' || errorMessage.includes('aborted')) {
        return;
      }
      
      toastRef.current.error(`Error: ${errorMessage}`, {
        autoClose: 10000,
      });
    };
    
    // Set up unhandled promise rejection handler
    const handleRejection = (event) => {
      const error = event.reason || 'An unexpected error occurred';
      const errorMessage = error.message || String(error);
      
      // Don't show notification for aborted requests
      if (error.name === 'AbortError' || errorMessage.includes('aborted')) {
        return;
      }
      
      toastRef.current.error(`Error: ${errorMessage}`, {
        autoClose: 10000,
      });
    };
    
    // Add event listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleRejection);
    
    // Clean up
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return (
    <div className="notification-container">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        containerId="notification-container"
        className="notification-toast-container"
        toastClassName="notification-toast"
        bodyClassName="notification-body"
        progressClassName="notification-progress"
        style={{
          zIndex: 999999,
          position: 'fixed',
          top: '20px',
          right: '20px',
          maxWidth: '400px',
          width: '100%',
          padding: '0 15px',
        }}
        toastStyle={{
          margin: '0 0 1rem 0',
          borderRadius: '8px',
          padding: '16px',
          fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          backgroundColor: '#ffffff',
          color: '#333333',
          minWidth: '300px',
          maxWidth: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
        bodyStyle={{
          margin: 0,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          color: '#333333',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
          lineHeight: '1.5',
        }}
        progressStyle={{
          background: 'rgba(0,0,0,0.1)',
        }}
      />
    </div>
  );
};

export default Notification;
